import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, Clock, ArrowLeft, CheckCircle, AlertTriangle, DollarSign } from "lucide-react";
import SharedNavbar from "../../components/SharedNavbar";

const THUMBNAIL = "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwYmlsbGluZyUyMGNvZGluZyUyMGhvc3BpdGFsJTIwY2hhcnQlMjBwYXBlcndvcmt8ZW58MHx8fHwxNzczNDM5ODM1fDA&ixlib=rb-4.1.0&q=85";

function useFadeIn(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, visible] = useFadeIn();
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

function Cpt({ code }) {
  return <span>{code}</span>;
}

function BulletList({ items, color = "blue" }) {
  const iconColor = color === "red" ? "text-red-400" : color === "green" ? "text-green-500" : "text-blue-500";
  const Icon = color === "red" ? AlertTriangle : CheckCircle;
  return (
    <ul className="space-y-2 mb-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-slate-700 text-base">
          <Icon size={16} className={`${iconColor} flex-shrink-0 mt-0.5`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionH2({ children, color = "blue" }) {
  const border = color === "red" ? "border-red-400" : color === "green" ? "border-green-500" : "border-blue-500";
  return <h2 className={`text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 ${border} pl-4`}>{children}</h2>;
}

function SectionH3({ children }) {
  return <h3 className="text-xl font-semibold text-slate-800 mb-2 mt-5">{children}</h3>;
}

function InlineCTA() {
  return (
    <div className="rounded-2xl p-7 my-10 text-center" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", boxShadow: "0 12px 40px rgba(29,78,216,0.18)" }}>
      <p className="text-white font-extrabold text-xl mb-2">Stop Calculating Manually.</p>
      <p className="text-slate-300 text-sm mb-5">Make More of what you Earn with the <span className="text-blue-400 font-semibold">Best RVU Tracker</span> in the market.</p>
      <a href="/register" data-testid="article-inline-cta" className="inline-flex items-center gap-2 text-sm font-bold text-white rounded-full px-7 py-3 transition-all hover:-translate-y-px hover:shadow-xl" style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 16px rgba(37,99,235,0.40)" }}>
        Try Doc RVU Tracker Free <ChevronRight size={15} />
      </a>
    </div>
  );
}

const ARTICLES_META = [
  { slug: "/rvu-articles/how-to-track-rvus", title: "How to Track RVUs: Complete Guide for Providers", desc: "Learn what RVUs are, why tracking matters, common mistakes, and the most effective approach to stay on top of your productivity.", category: "RVU Fundamentals", readTime: "6 min", badge: "bg-blue-50 text-blue-700 border-blue-200", img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwcm92aWRlciUyMG1lZGljYWwlMjB0YWJsZXQlMjBkYXRhJTIwdHJhY2tpbmclMjBjbGluaWNhbHxlbnwwfHx8fDE3NzMzNjQ5Mzh8MA&ixlib=rb-4.1.0&q=85" },
  { slug: "/rvu-articles/missed-billing-codes", title: "Most Commonly Missed Billing Codes", desc: "Discover the CPT codes most frequently missed in urgent care, primary care, and the ER — and how to avoid lost income.", category: "Billing & Coding", readTime: "10 min", badge: "bg-orange-50 text-orange-700 border-orange-200", img: "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwYmlsbGluZyUyMGNvZGluZyUyMGhvc3BpdGFsJTIwY2hhcnQlMjBwYXBlcndvcmt8ZW58MHx8fHwxNzczNDM5ODM1fDA&ixlib=rb-4.1.0&q=85" },
  { slug: "/rvu-articles/rvus-and-paycheck", title: "How RVUs Translate Into Your Paycheck", desc: "Understand how wRVUs directly convert to compensation, what models exist, and how to take control of your earnings.", category: "RVU & Compensation", readTime: "8 min", badge: "bg-green-50 text-green-700 border-green-200", img: "https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwaHlzaWNpYW4lMjBjb250cmFjdCUyMG5lZ290aWF0aW9uJTIwaW5jb21lJTIwZmluYW5jaWFsJTIwcGxhbm5pbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczNTE0OTQ3fDA&ixlib=rb-4.1.0&q=85" },
];

function TableOfContents({ items }) {
  return (
    <nav className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-10" aria-label="Table of contents">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">In This Article</p>
      <ol className="space-y-1.5 list-none p-0 m-0">
        {items.map((item, i) => (
          <li key={item.id}>
            <a href={`#${item.id}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors">
              <span className="text-slate-400 text-xs min-w-[1.25rem]">{i + 1}.</span>
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function RelatedArticles({ currentSlug }) {
  const related = ARTICLES_META.filter((a) => a.slug !== currentSlug);
  return (
    <section className="mb-10 mt-2" aria-label="Related articles">
      <h2 className="text-2xl font-bold text-slate-900 mb-5 tracking-tight">Continue Reading</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {related.map((article) => (
          <a key={article.slug} href={article.slug} className="group block rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-blue-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 no-underline">
            <div className="aspect-[16/9] overflow-hidden">
              <img src={article.img} alt={article.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${article.badge}`}>{article.category}</span>
                <span className="text-xs text-slate-400">{article.readTime} read</span>
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-2 group-hover:text-blue-600 transition-colors leading-snug">{article.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{article.desc}</p>
              <div className="mt-3 flex items-center gap-1 text-blue-600 text-sm font-semibold">Read Article <ChevronRight size={13} /></div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export default function MissedBillingCodes() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Most Commonly Missed Billing Codes in Urgent Care, Primary Care & ER | Doc RVU Tracker";
    const managed = [];
    function setMeta(selector, attr, val, content) {
      let el = document.querySelector(selector);
      const existed = !!el;
      const prevContent = existed ? el.getAttribute("content") : null;
      if (!existed) { el = document.createElement("meta"); document.head.appendChild(el); }
      el.setAttribute(attr, val); el.setAttribute("content", content);
      managed.push({ el, existed, prevContent });
    }
    setMeta('meta[name="description"]', "name", "description", "Discover the most commonly missed CPT billing codes in urgent care, primary care, and emergency medicine — and how real-time RVU tracking prevents thousands in lost income.");
    setMeta('meta[name="keywords"]', "name", "keywords", "missed billing codes, commonly missed CPT codes, urgent care billing codes, emergency medicine CPT codes, primary care billing, missed RVU codes, underbilling physician");
    setMeta('meta[property="og:title"]', "property", "og:title", "Most Commonly Missed Billing Codes in Urgent Care, Primary Care & ER");
    setMeta('meta[property="og:description"]', "property", "og:description", "Discover the most commonly missed CPT billing codes in urgent care, primary care, and emergency medicine — and how tracking your RVUs can prevent lost income.");
    setMeta('meta[property="og:type"]', "property", "og:type", "article");
    setMeta('meta[property="og:url"]', "property", "og:url", `${window.location.origin}/rvu-articles/missed-billing-codes`);
    setMeta('meta[property="og:image"]', "property", "og:image", "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?w=1200&q=80");
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Doc RVU Tracker");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", "Most Commonly Missed Billing Codes in Urgent Care, Primary Care & ER");
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", "Discover the most commonly missed CPT billing codes in urgent care, primary care, and emergency medicine.");
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?w=1200&q=80");
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalExisted = !!canonical;
    const prevHref = canonicalExisted ? canonical.getAttribute("href") : null;
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${window.location.origin}/rvu-articles/missed-billing-codes`);
    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Article", "headline": "Most Commonly Missed Billing Codes in Urgent Care, Primary Care & ER", "description": "A guide to the most commonly missed billing codes in urgent care, primary care, and emergency medicine — and how tracking your RVUs can prevent lost income.", "author": { "@type": "Organization", "name": "Doc RVU Tracker", "url": window.location.origin }, "publisher": { "@type": "Organization", "name": "Doc RVU Tracker", "url": window.location.origin, "logo": { "@type": "ImageObject", "url": `${window.location.origin}/rvu-logo.png` } }, "datePublished": "2026-03-01", "dateModified": "2026-03-01", "image": "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?w=1200&q=80", "url": `${window.location.origin}/rvu-articles/missed-billing-codes`, "mainEntityOfPage": { "@type": "WebPage", "@id": `${window.location.origin}/rvu-articles/missed-billing-codes` } });
    document.head.appendChild(jsonLd);
    return () => {
      document.title = prevTitle;
      managed.forEach(({ el, existed, prevContent }) => { if (existed) el.setAttribute("content", prevContent); else el.parentNode && el.parentNode.removeChild(el); });
      if (canonicalExisted) canonical.setAttribute("href", prevHref); else canonical.parentNode && canonical.parentNode.removeChild(canonical);
      jsonLd.parentNode && jsonLd.parentNode.removeChild(jsonLd);
    };
  }, []);

  return (
    <div className="min-h-screen font-sans antialiased" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", background: "#f8fafc" }}>
      <SharedNavbar activePage="articles" />

      {/* Article Header */}
      <section className="pt-28 pb-0 px-4" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <a href="/rvu-articles" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 mb-6 transition-colors" data-testid="back-to-articles">
            <ArrowLeft size={15} /> Back to Articles
          </a>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold px-3 py-1 rounded-full border bg-orange-50 text-orange-700 border-orange-200">Billing & Coding</span>
            <span className="flex items-center gap-1 text-xs text-slate-400"><Clock size={11} /> 10 min read</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
            Most Commonly Missed Billing Codes
            <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}> (Primary Care, Urgent Care, & ER)</span>
          </h1>
        </div>
      </section>

      {/* Hero image */}
      <div className="px-4 pb-10" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-md" style={{ maxHeight: 380 }}>
            <img src={THUMBNAIL} alt="Medical billing codes and CPT documentation for urgent care and emergency medicine" loading="lazy" className="w-full object-cover" style={{ height: 380, objectPosition: "center" }} />
          </div>
        </div>
      </div>

      {/* Article Body */}
      <article className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">

          {/* Intro */}
          <FadeIn>
            <div className="mb-10">
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                In busy clinical environments like urgent care, primary care, and emergency medicine, providers are constantly balancing patient care, documentation, throughput, and administrative expectations.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Billing accuracy often becomes a secondary priority — not because providers don't care, but because there simply isn't enough time.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">However, missed billing opportunities can significantly impact provider compensation, RVU productivity metrics, practice revenue, contract negotiations, and performance evaluations.</p>
              <p className="text-slate-600 text-sm p-4 bg-orange-50 border border-orange-100 rounded-xl font-medium text-orange-800">
                Even small missed codes can compound into thousands of dollars of lost productivity annually.
              </p>
              <p className="text-slate-700 leading-relaxed mt-4">
                This article reviews some of the most commonly missed billing opportunities across urgent care, primary care, and emergency medicine.
              </p>
            </div>
          </FadeIn>

          {/* Table of Contents */}
          <FadeIn delay={30}>
            <TableOfContents items={[
              { id: "why-missed-billing-matters", label: "Why Missed Billing Codes Matter" },
              { id: "urgent-care", label: "Missed Codes in Urgent Care" },
              { id: "primary-care", label: "Missed Codes in Primary Care" },
              { id: "emergency-medicine", label: "Missed Codes in Emergency Medicine" },
              { id: "hidden-cost", label: "The Hidden Cost of Missed Billing" },
              { id: "how-tracking-helps", label: "How RVU Tracking Helps" },
              { id: "track-with-one-click", label: "Track RVUs with Just 1 Click" },
              { id: "final-thoughts", label: "Final Thoughts" },
            ]} />
          </FadeIn>

          {/* Why Missed Billing Codes Matter */}
          <FadeIn delay={60}>
            <section id="why-missed-billing-matters" className="mb-10 scroll-mt-24">
              <SectionH2>Why Missed Billing Codes Matter</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-3">RVUs are not just abstract productivity metrics. They directly influence how much providers get paid, how practices get reimbursed, how productivity is measured, and bonus eligibility and contract leverage.</p>
              <p className="text-slate-700 leading-relaxed mb-3">For salary or hourly providers, RVU production still matters. Higher productivity often translates into stronger performance evaluations, greater negotiation leverage for compensation, increased bonus eligibility, and greater perceived clinical value within the organization.</p>
              <p className="text-slate-600 text-sm italic p-4 bg-slate-50 border border-slate-200 rounded-xl">
                Missed codes don't just affect the practice — they affect your income trajectory.
              </p>
            </section>
          </FadeIn>

          {/* Urgent Care */}
          <FadeIn delay={60}>
            <section id="urgent-care" className="mb-10 scroll-mt-24">
              <SectionH2>Most Commonly Missed Codes in Urgent Care</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Urgent care environments are high-throughput settings where providers move rapidly between patients. This workflow frequently leads to underbilling of procedures and ancillary services.
              </p>

              <SectionH3>Cerumen Removal During Ear Complaint Visits</SectionH3>
              <p className="text-slate-700 text-base leading-relaxed mb-2">Patients commonly present to urgent care with ear fullness, hearing loss, otalgia, or cerumen impaction.</p>
              <p className="text-slate-700 text-base leading-relaxed mb-2">
                Providers often bill only for the evaluation and management visit (<Cpt code="99202–99215" />) and fail to bill for cerumen removal (<Cpt code="69210" />) when performed.
              </p>
              <p className="text-slate-700 text-base leading-relaxed mb-2">
                Additionally, providers frequently perform bilateral cerumen removal but bill only once.
              </p>
              <p className="text-slate-700 text-sm leading-relaxed mb-4">
                When clinically appropriate and documented, cerumen removal (<Cpt code="69210" />) may be reported for each ear depending on payer policies.
              </p>
              <p className="text-slate-600 text-base p-3 bg-blue-50 border border-blue-100 rounded-xl mb-2">
                This represents one of the most common missed procedural billing opportunities in urgent care.
              </p>

              <SectionH3>Immunization Administration During Laceration Visits</SectionH3>
              <p className="text-slate-700 text-base leading-relaxed mb-2">A frequent urgent care scenario:</p>
              <BulletList items={["Patient presents with a laceration", "Wound repair performed (12001–13160 depending on complexity)", "Tdap booster administered"]} />
              <p className="text-slate-700 text-base font-medium mb-2">Providers often forget to bill:</p>
              <BulletList items={["Immunization administration (90471)", "Vaccine product (90715 for Tdap)"]} color="red" />
              <p className="text-slate-600 text-base italic mb-4">This results in underreported procedural productivity.</p>

              <SectionH3>Splinting and Orthopedic Immobilization</SectionH3>
              <p className="text-slate-700 text-base mb-2">Providers frequently perform:</p>
              <BulletList items={["Splint application (29125, 29130, 29515, etc.)", "Immobilization for fractures or sprains"]} />
              <p className="text-slate-700 text-base font-medium mb-2">But fail to bill:</p>
              <BulletList items={["Splint application procedure (29125, 29130, 29515, 29505)", "Appropriate supply codes when applicable"]} color="red" />

              <SectionH3>Nebulizer Treatments</SectionH3>
              <p className="text-slate-700 text-base mb-2">Common urgent care oversight:</p>
              <BulletList items={["Administering bronchodilator therapy", "Failing to bill nebulizer treatment (94640)"]} color="red" />
              <p className="text-slate-600 text-base italic mb-4">Repeat treatments may also be underreported.</p>

              <SectionH3>Injection Administration</SectionH3>
              <p className="text-slate-700 text-base mb-2">Urgent care providers commonly administer:</p>
              <BulletList items={["Ketorolac", "Corticosteroids", "Antibiotics", "Antiemetics"]} />
              <p className="text-slate-700 text-base font-medium mb-2">Yet may forget to bill:</p>
              <BulletList items={["Injection administration (96372)"]} color="red" />
              <p className="text-slate-600 text-sm italic">This is especially common during high-volume shifts.</p>
            </section>
          </FadeIn>

          {/* Inline CTA after Urgent Care section */}
          <FadeIn delay={60}><InlineCTA /></FadeIn>

          {/* Primary Care */}
          <FadeIn delay={60}>
            <section id="primary-care" className="mb-10 scroll-mt-24">
              <SectionH2>Most Commonly Missed Codes in Primary Care</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Primary care visits often involve preventive care, chronic disease management, and routine immunizations. Billing opportunities may be missed when multiple services are provided in a single visit.
              </p>

              <SectionH3>Preventive Visits with Problem-Oriented Care</SectionH3>
              <p className="text-slate-700 text-base mb-2">Providers frequently address:</p>
              <BulletList items={["Acute complaints during annual exams"]} />
              <p className="text-slate-700 text-base font-medium mb-2">But fail to appropriately bill:</p>
              <BulletList items={["Preventive service (99381–99397)", "Problem-oriented E/M visit (99212–99215 with modifier -25 when appropriate)"]} color="red" />

              <SectionH3>Routine Immunizations</SectionH3>
              <p className="text-slate-700 text-base mb-2">Primary care providers regularly administer vaccines such as:</p>
              <BulletList items={["Influenza (90686, 90688)", "Pneumococcal (90677, 90732)", "Shingles (90750)", "HPV (90651)", "Hepatitis B (90746)"]} />
              <p className="text-slate-700 text-base font-medium mb-2">However, missed billing opportunities include:</p>
              <BulletList items={["Immunization administration (90471, 90472 for additional vaccines)", "Vaccine product billing"]} color="red" />
              <p className="text-slate-600 text-base italic mb-4">When multiple vaccines are administered in one visit, failure to bill additional administration codes is common.</p>

              <SectionH3>Chronic Care Complexity Underrepresentation</SectionH3>
              <p className="text-slate-700 text-base mb-2">Providers may manage:</p>
              <BulletList items={["Multiple chronic conditions", "Medication adjustments", "Preventive counseling"]} />
              <p className="text-slate-600 text-sm p-3 bg-orange-50 border border-orange-100 rounded-xl">
                But document at a level that does not reflect true complexity, leading to lower E/M coding and reduced RVU productivity.
              </p>
            </section>
          </FadeIn>

          {/* Emergency Medicine */}
          <FadeIn delay={60}>
            <section id="emergency-medicine" className="mb-10 scroll-mt-24">
              <SectionH2>Most Commonly Missed Codes in Emergency Medicine</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Emergency department workflows are among the most cognitively demanding clinical environments. Providers must manage high-acuity patients, frequent interruptions, and rapid clinical decision-making.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                This increases the likelihood of missed procedural and time-based billing.
              </p>

              <SectionH3>Laceration Repair Complexity Underestimation</SectionH3>
              <p className="text-slate-700 text-base mb-2">Providers may perform:</p>
              <BulletList items={["Intermediate repair (12031–12057)", "Complex repair (13100–13160)"]} />
              <p className="text-slate-700 text-base font-medium mb-2">But bill only:</p>
              <BulletList items={["Simple repair (12001–12021)"]} color="red" />
              <p className="text-slate-600 text-base italic mb-4">This underrepresents procedural effort and RVU production.</p>

              <SectionH3>Critical Care Time Underreporting</SectionH3>
              <p className="text-slate-700 text-base mb-2">Providers frequently deliver:</p>
              <BulletList items={["Continuous high-acuity management", "Complex decision-making", "Frequent reassessments"]} />
              <p className="text-slate-700 text-base font-medium mb-2">Yet fail to properly track cumulative critical care time (<Cpt code="99291" />, <Cpt code="99292" />).</p>
              <p className="text-slate-600 text-sm p-3 bg-red-50 border border-red-100 rounded-xl text-red-700 font-medium mb-4">
                This represents one of the largest missed RVU opportunities in emergency medicine.
              </p>

              <SectionH3>Procedural Sedation</SectionH3>
              <p className="text-slate-700 text-base mb-2">Providers may perform:</p>
              <BulletList items={["Sedation for fracture reduction or cardioversion"]} />
              <p className="text-slate-700 text-base font-medium mb-2">But fail to bill:</p>
              <BulletList items={["Procedural sedation (99152–99157)"]} color="red" />
            </section>
          </FadeIn>

          {/* Hidden Cost */}
          <FadeIn delay={60}>
            <section id="hidden-cost" className="mb-10 scroll-mt-24">
              <SectionH2 color="red">The Hidden Cost of Missed Billing</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-3">Missed billing opportunities can result in:</p>
              <div className="space-y-2.5 mb-5">
                {["Underreported productivity", "Lower RVU totals", "Reduced bonus potential", "Weaker contract negotiation positioning", "Misalignment between clinical effort and compensation"].map((item) => (
                  <div key={item} className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <DollarSign size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 text-sm italic p-4 bg-slate-50 border border-slate-200 rounded-xl mb-4">
                Over time, these missed opportunities can represent significant lost income.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">Providers often assume billing systems or coding departments will capture all services. In reality, accurate billing begins with provider awareness, proper documentation, and personal productivity tracking.</p>
            </section>
          </FadeIn>

          {/* How Tracking Helps */}
          <FadeIn delay={60}>
            <section id="how-tracking-helps" className="mb-10 scroll-mt-24">
              <SectionH2 color="green">How Tracking Your RVUs Helps Prevent Missed Income</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-3">When providers actively track their patient encounters and procedures, they gain immediate awareness of productivity, insight into missed billing patterns, greater financial clarity, data to support compensation discussions, and confidence in income accuracy.</p>
              <p className="text-slate-700 leading-relaxed mb-3">In fast-paced clinical environments, tracking must be:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {["Quick", "Easy", "Non-disruptive", "Workflow-friendly"].map((item) => (
                  <div key={item} className="bg-green-50 border border-green-100 rounded-xl px-3 py-3 text-center">
                    <p className="text-green-700 font-semibold text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </section>
          </FadeIn>

          {/* CTA Section — "Track Patient Visits & RVUs with Just 1 Click" */}
          <FadeIn delay={60}>
            <section id="track-with-one-click" className="mb-10 scroll-mt-24">
              <SectionH2>Track Patient Visits &amp; RVUs with Just 1 Click</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-2">You're moving room to room.</p>
              <p className="text-slate-700 leading-relaxed mb-2">You're charting between patients.</p>
              <p className="text-slate-700 leading-relaxed mb-4">You're managing high cognitive load all shift.</p>
              <p className="text-slate-600 text-base italic mb-4">You don't have time to meticulously track every billing opportunity.</p>
              <p className="text-slate-700 leading-relaxed mb-4">But you still deserve to know what you earned, ensure accurate productivity reporting, and make sure you're getting paid what you earned and deserve.</p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong className="text-slate-900">Doc RVU Tracker</strong> was built by a practicing clinician to make RVU tracking quick and effortless.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">With just one click, you can log patient visits instantly, track procedures and RVU productivity, monitor earnings per shift, and identify patterns that may indicate missed billing opportunities.</p>
              <p className="text-slate-600 text-base italic mb-4">All without slowing down your workflow.</p>
              <p className="text-slate-700 leading-relaxed mb-4">Because your focus should be on patient care — not spreadsheets.</p>
              <p className="text-slate-600 text-sm p-4 bg-blue-50 border border-blue-100 rounded-xl">
                Ready to start?{" "}
                <a href="/register" data-testid="article-cta-main" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                  Start tracking for free with Doc RVU Tracker →
                </a>
              </p>
            </section>
          </FadeIn>

          {/* Final Thoughts */}
          <FadeIn delay={60}>
            <section id="final-thoughts" className="mb-10 scroll-mt-24">
              <SectionH2>Final Thoughts</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-4">
                In fast-paced clinical environments, missed billing opportunities are rarely due to lack of knowledge. More often, they result from time constraints, workflow demands, and the cognitive load of managing patient care.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Even small oversights can accumulate into thousands of dollars in losses in RVU productivity and compensation over time.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Maintaining awareness of common missed codes and modifier nuances can help ensure that your clinical work is accurately reflected in both productivity metrics and reimbursement.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                However, tracking this consistently during a busy shift can be challenging.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Doc RVU Tracker was designed to make RVU tracking quick and effortless — so you can focus on patient care while still ensuring you're getting paid what you earned and deserve.
              </p>
              <p className="text-slate-700 leading-relaxed">
                With just one click, you can log visits, monitor productivity trends, and gain clarity on your real earnings per shift.
              </p>
            </section>
          </FadeIn>

          {/* Related Articles */}
          <FadeIn delay={60}>
            <RelatedArticles currentSlug="/rvu-articles/missed-billing-codes" />
          </FadeIn>

          {/* Final CTA */}
          <FadeIn delay={60}>
            <div className="rounded-2xl p-8 text-center mt-4" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #0369a1 100%)", boxShadow: "0 20px 60px rgba(29,78,216,0.25)" }}>
              <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 56, height: 56, borderRadius: "14px", objectFit: "cover", boxShadow: "0 4px 16px rgba(22,163,74,0.40)", margin: "0 auto 16px" }} />
              <h3 className="text-2xl font-extrabold text-white mb-2 tracking-tight">Start tracking your next shift today.</h3>
              <p className="text-blue-100 text-base mb-6">Make up to Thousands More - for just a low monthly price.</p>
              <a href="/register" data-testid="article-final-cta" className="inline-flex items-center gap-2 text-base font-bold text-white rounded-full px-8 py-3.5 transition-all hover:-translate-y-px hover:shadow-xl" style={{ background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.40)" }}>
                Get Started at DocRVUTracker.com <ChevronRight size={18} />
              </a>
            </div>
          </FadeIn>

        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2.5 mb-1.5">
              <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 30, height: 30, borderRadius: "7px", objectFit: "cover" }} />
              <span className="text-white font-semibold text-sm">Doc RVU Tracker</span>
            </div>
            <p className="text-xs text-slate-500">The One-Click tracker built for providers by providers.</p>
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} Doc RVU Tracker. All rights reserved.</p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-1">
            <div className="flex gap-5 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <a href="/rvu-articles" className="hover:text-white transition-colors">Articles</a>
              <a href="/free-rvu-calculator" className="hover:text-white transition-colors">Free Calculator</a>
              <a href="/login" className="hover:text-white transition-colors">Log In</a>
            </div>
            <p className="text-sm font-medium text-slate-300 mt-1">DocRVUTracker@gmail.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
